import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

class RecentlyViewedProductsSection extends HTMLElement {
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#loadProducts();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  #activeFetch = null;
  #swiperInstance = null;

  connectedCallback() {
    this.style.display = 'block';
    const rect = this.getBoundingClientRect();
    const isVisible = rect.top < window.innerHeight + 400;
    if (isVisible) {
      setTimeout(() => this.#loadProducts(), 100);
    } else {
      this.#intersectionObserver.observe(this);
    }
    this.#initSwiperWhenReady();
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#activeFetch?.abort();
    this.#destroySwiper();
  }

  #destroySwiper() {
    if (this.#swiperInstance) {
      this.#swiperInstance.destroy(true, true);
      this.#swiperInstance = null;
    }
  }

  #initSwiperWhenReady() {
    const run = () => {
      const wrap = this.querySelector('.recently-viewed-products__swiper-wrap');
      const swiperEl = this.querySelector('.recently-viewed-products__swiper');
      if (!wrap || !swiperEl || wrap.getAttribute('data-has-products') !== 'true') return;
      const hasSlides = swiperEl.querySelector('.swiper-wrapper')?.children?.length > 0;
      if (!hasSlides) return;
      if (typeof window.Swiper !== 'function') {
        requestAnimationFrame(run);
        return;
      }
      this.#initSwiper(swiperEl, wrap);
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => setTimeout(run, 50));
    } else {
      setTimeout(run, 50);
    }
  }

  #initSwiper(swiperEl, wrap) {
    this.#destroySwiper();
    const showArrows = this.dataset.showArrows === 'true';
    const showPagination = this.dataset.showPagination === 'true';
    const prevEl = wrap.querySelector('.recently-viewed-products__arrow--prev');
    const nextEl = wrap.querySelector('.recently-viewed-products__arrow--next');
    const paginationEl = wrap.querySelector('.recently-viewed-products__pagination');

    this.#swiperInstance = new window.Swiper(swiperEl, {
      slidesPerView: 2.2,
      slidesPerGroup: 1,
      spaceBetween: 20,
      centeredSlides: false,
      loop: false,
      breakpoints: {
        750: {
          slidesPerView: 3,
          spaceBetween: 30,
        },
        1200: {
          slidesPerView: 4,
          spaceBetween: 30,
        },
      },
      navigation: showArrows && prevEl && nextEl
        ? { nextEl, prevEl }
        : false,
      pagination:
        showPagination && paginationEl
          ? { el: paginationEl, clickable: true }
          : false,
      on: {
        init() {
          swiperEl.classList.add('swiper-initialized');
          const slideCount = this.slides?.length ?? 0;
          const perView = typeof this.params.slidesPerView === 'number'
            ? this.params.slidesPerView
            : 4;
          if (slideCount <= perView) {
            wrap.classList.add('recently-viewed-products__swiper-wrap--no-nav');
          }
        },
      },
    });
  }

  async #loadProducts() {
    const { sectionId, currentProductId, maxProducts } = this.dataset;
    if (!sectionId) return;

    if (!RecentlyViewed || typeof RecentlyViewed.getProducts !== 'function') {
      this.style.display = 'none';
      return;
    }

    const viewedProductIds = RecentlyViewed.getProducts();
    const filteredIds = viewedProductIds.filter((id) => id !== currentProductId);
    const productIds = filteredIds.slice(0, parseInt(maxProducts || '6', 10));

    if (productIds.length === 0) {
      this.style.display = 'none';
      return;
    }

    try {
      const searchBaseUrl =
        typeof window.Theme !== 'undefined' && window.Theme.routes?.search_url
          ? window.Theme.routes.search_url
          : '/search';
      const url = new URL(searchBaseUrl, window.location.origin);
      const searchQuery = productIds.map((id) => `id:${id}`).join(' OR ');
      url.searchParams.set('q', searchQuery);
      url.searchParams.set('type', 'product');

      this.#activeFetch = new AbortController();

      let sectionHTML;
      try {
        sectionHTML = await sectionRenderer.getSectionHTML(sectionId, false, url);
      } catch (fetchError) {
        console.error('Recently viewed: fetch error', fetchError);
        this.style.display = 'none';
        return;
      }

      if (this.#activeFetch.signal.aborted) return;
      if (!sectionHTML || sectionHTML.trim().length === 0) {
        this.style.display = 'none';
        return;
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHTML, 'text/html');
      const sectionElement =
        doc.querySelector(`recently-viewed-products[id="${this.id}"]`) ||
        doc.querySelector('recently-viewed-products');

      if (!sectionElement) {
        this.style.display = 'none';
        return;
      }

      const productList = sectionElement.querySelector(
        '.recently-viewed-products__swiper-wrap[data-has-products="true"]'
      );
      if (!productList) {
        const anyList = sectionElement.querySelector('.recently-viewed-products__swiper-wrap');
        if (!anyList || !anyList.querySelector('.swiper-slide')) {
          this.style.display = 'none';
          return;
      }
      }

      this.dataset.hasProducts = 'true';
      this.innerHTML = sectionElement.innerHTML;

      // Re-initialize Smart Wishlist buttons for newly injected product cards
      if (typeof window !== 'undefined' && typeof window.ReloadSmartWishlist === 'function') {
        try {
          window.ReloadSmartWishlist();
        } catch (e) {
          console.error('Recently viewed section: ReloadSmartWishlist failed', e);
        }
      }

      setTimeout(() => {
        this.style.display = 'block';
        this.style.visibility = 'visible';
        this.style.opacity = '1';
        const wrap = this.querySelector('.recently-viewed-products__swiper-wrap');
        if (wrap && wrap.querySelector('.swiper-wrapper')?.children?.length > 0) {
          wrap.setAttribute('data-has-products', 'true');
          this.#initSwiperWhenReady();
        }
      }, 50);
    } catch (error) {
      if (this.#activeFetch?.signal.aborted) return;
      console.error('Recently viewed products error:', error);
      this.style.display = 'none';
    } finally {
      this.#activeFetch = null;
    }
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProductsSection);
}
